import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle, ArrowRight, Plus, Pencil, Check, X, Trash2, RotateCcw, Truck, ArrowUpCircle, MoreVertical, MailPlus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const API = '/api';

const STAGE_LABEL = {
  queued: 'Queued',
  classifying: 'Classifying buckets',
  sub_classifying: 'Tagging sub-categories',
  matching: 'Matching against CRM',
  auditing: 'CRO/service audit',
  excluding: 'Applying exclusion rules',
  web_validating: 'Web-validating low-confidence rows',
  committing_companies: 'Auto-committing companies',
  matching_assets: 'Matching assets to biotechs',
  committing_assets: 'Auto-committing assets',
  ready: 'Ready for review',
  done: 'Imported',
  error: 'Error',
  empty: 'Empty',
};

const STAGE_PROGRESS_ORDER = ['queued', 'classifying', 'sub_classifying', 'matching', 'auditing', 'excluding', 'web_validating', 'committing_companies', 'matching_assets', 'committing_assets', 'done'];
const RUNNING_STAGES = ['queued', 'classifying', 'sub_classifying', 'matching', 'auditing', 'excluding', 'web_validating', 'committing_companies', 'matching_assets', 'committing_assets'];

const StageBadge = ({ job }) => {
  if (!job) return null;
  const stage = job.stage || 'ready';
  if (stage === 'ready') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="stage-ready"><CheckCircle2 className="w-3 h-3 mr-1" />Ready</Badge>;
  if (stage === 'done') return <Badge className="bg-emerald-600 text-white border-emerald-600" data-testid="stage-done"><CheckCircle2 className="w-3 h-3 mr-1" />Imported</Badge>;
  if (stage === 'error') return <Badge className="bg-rose-100 text-rose-700 border-rose-200" title={job.error || ''}><AlertCircle className="w-3 h-3 mr-1" />Error</Badge>;
  if (stage === 'cancelled') return <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid="stage-cancelled"><X className="w-3 h-3 mr-1" />Cancelled</Badge>;
  const idx = STAGE_PROGRESS_ORDER.indexOf(stage);
  const pct = idx >= 0 ? Math.round(((idx + 1) / STAGE_PROGRESS_ORDER.length) * 100) : 0;
  return (
    <Badge className="bg-sky-100 text-sky-700 border-sky-200" data-testid={`stage-${stage}`}>
      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
      {STAGE_LABEL[stage] || stage} {job.stage_progress != null && job.stage_total ? `${job.stage_progress}/${job.stage_total}` : pct + '%'}
    </Badge>
  );
};

export default function ImportHubPage() {
  const [imports, setImports] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/imports`, { withCredentials: true });
      setImports(res.data.items || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  // Poll while any import is in-flight
  useEffect(() => {
    const inflight = imports.some(i => (i.job && i.job.stage && !['ready', 'done', 'error', 'empty', 'cancelled'].includes(i.job.stage))
      || i.job?.people_backfill?.status === 'running');
    if (inflight) {
      pollRef.current = setInterval(load, 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [imports]); // eslint-disable-line

  const downloadExport = async (tag, onlyApproved) => {
    try {
      const res = await axios.get(`${API}/imports/${tag}/export?only_approved=${onlyApproved ? 1 : 0}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `${tag}_${onlyApproved ? 'approved' : 'all'}_${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${onlyApproved ? 'approved' : 'all'} rows`);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const cancelImport = async (tag) => {
    if (!window.confirm(`Cancel the running classification for "${tag}"?\n\nThe pipeline will stop at the next batch boundary (a few seconds).`)) return;
    try {
      await axios.post(`${API}/imports/${tag}/cancel`, {}, { withCredentials: true });
      toast.success('Cancel signal sent — pipeline will stop shortly');
      load();
    } catch (e) {
      toast.error('Cancel failed');
    }
  };

  const deleteImport = async (tag) => {
    if (!window.confirm(`Delete the staging rows for "${tag}"?\n\nCommitted rows already in your CRM stay untouched. Only the un-pushed staging entries are dropped.`)) return;
    try {
      const r = await axios.delete(`${API}/imports/${tag}`, { withCredentials: true });
      toast.success(`Deleted ${r.data.deleted_staging_rows} staging rows · ${r.data.preserved_committed_rows} committed rows preserved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Re-parse the original Excel to backfill emails on staging rows +
  // committed biotechs when the parser mis-detected the email column.
  // Two modes:
  //   • Zero-click: hit the endpoint without a file → backend pulls the
  //     persisted source from GridFS (default for uploads ≥Feb-2026).
  //   • Fallback: if the backend returns 400 (no stored source — legacy
  //     pre-persistence import), open a file picker so the user can
  //     supply the same Excel manually.
  const triggerBackfillPeople = async (tag) => {
    if (!window.confirm(
      `Backfill named contacts for "${tag}"?\n\n` +
      `Re-reads the stored sheet and adds the named people it carries (CEO, named BD lead, BD/licensing delegates — usually without email) ` +
      `as visible contacts on the biotech / pharma records already committed from this import. Never duplicates; safe to re-run.`
    )) return;
    try {
      await axios.post(`${API}/imports/${tag}/backfill-people`, {}, { withCredentials: true });
      toast.success('Backfill started — progress shows under the conference row.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    }
  };

  const triggerBackfillEmails = async (tag) => {
    const runWithFile = (file) => {
      const fd = new FormData();
      if (file) fd.append('file', file);
      const t = toast.loading(file
        ? `Re-parsing ${file.name} to backfill emails…`
        : `Replaying stored source file to backfill emails…`);
      return axios.post(
        `${API}/imports/${tag}/backfill-emails`,
        file ? fd : null,
        { withCredentials: true, headers: file ? { 'Content-Type': 'multipart/form-data' } : {}, timeout: 180000 },
      ).then((r) => {
        toast.dismiss(t);
        const d = r.data || {};
        if (!d.rows_in_file_with_email) {
          toast.warning(d.message || 'No emails found in source file.', { duration: 10000 });
        } else {
          toast.success(
            `Backfilled ${d.staging_rows_patched ?? 0} staging rows and ${d.biotechs_patched ?? 0} live biotech profiles ` +
            `from ${d.source || 'source file'}. ` +
            `(${d.rows_in_file_with_email} rows in file had emails; ${d.unmatched_count ?? 0} couldn’t be matched by name.)`,
            { duration: 14000 },
          );
        }
        load();
      }).catch((err) => {
        toast.dismiss(t);
        if (err?.response?.status === 400 && !file) {
          // Legacy upload — no stored source. Fall back to manual file picker.
          toast.info('No stored source file for this import — pick the Excel manually.', { duration: 6000 });
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.xlsx,.xls,.csv';
          input.onchange = (e) => {
            const f = e.target.files?.[0];
            if (f) runWithFile(f);
          };
          input.click();
        } else {
          toast.error(err?.response?.data?.detail || 'Backfill failed');
        }
      });
    };
    // First attempt: zero-click — try GridFS source replay.
    return runWithFile(null);
  };

  const resumeImport = async (tag) => {
    if (!window.confirm(
      `Resume the classification pipeline for "${tag}"?\n\n` +
      `It will pick up exactly where it left off — rows that were already classified will be skipped. ` +
      `Safe to run any time the import is cancelled, errored, or appears stuck.`
    )) return;
    try {
      const r = await axios.post(`${API}/imports/${tag}/resume`, {}, { withCredentials: true });
      if (r.data?.resumed) {
        toast.success(`Pipeline resumed from stage "${r.data.from_stage}"`);
      } else {
        toast.info(r.data?.reason || 'Nothing to resume');
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resume failed');
    }
  };

  // Re-classify uncommitted pharma / service / cro_cdmo / unclear rows
  // through the updated AI classifier (which now knows about the
  // `distributor` bucket). Runs in the background on the server (~15-20
  // min for a 700-row sheet). Safe to run on any historical import.
  const rescanDistributors = async (tag) => {
    if (!window.confirm(
      `Rescan "${tag}" for distributors?\n\n` +
      `This re-runs the AI classifier on every row in this import currently bucketed as ` +
      `pharma / service / cro_cdmo / unclear — and flips any that are actually ` +
      `regional commercial partners (distributors) into a dedicated "distributor" bucket.\n\n` +
      `• Committed rows are NOT re-written. Use "Sync Distributor Category" after this finishes ` +
      `to promote already-committed pharma rows to distributor in your live CRM.\n` +
      `• Newly-classified distributors will appear in the staging review screen for you to approve & commit.\n` +
      `• Takes ~15–20 minutes for a 700-row sheet (runs in the background — safe to navigate away).`
    )) return;
    try {
      const r = await axios.post(`${API}/imports/${tag}/rescan-distributors`, {}, { withCredentials: true });
      toast.success(
        `Rescan started — ${r.data?.rows_reset ?? 0} rows queued for re-classification. ` +
        `Pipeline will run in the background; refresh this page to track progress.`,
        { duration: 9000 }
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rescan failed');
    }
  };

  // After a distributor rescan, some already-committed rows may have
  // flipped bucket from `pharma` → `distributor` in staging — but the
  // live company_profiles record still carries `company_type=pharma`
  // with no `category`. This call promotes those records to
  // `category=regional_distributor` (and `company_type=distributor`
  // when the existing value is "pharma"). Idempotent — never demotes.
  const syncDistributorCategory = async (tag) => {
    if (!window.confirm(
      `Sync "${tag}" distributor category to live CRM?\n\n` +
      `For every row in this import that was committed (pushed into company_profiles) AND has since ` +
      `been re-classified as a distributor (via the AI rescan above), this will:\n\n` +
      `  • Set category = "regional_distributor"\n` +
      `  • Set company_type = "distributor" (only if currently "pharma")\n` +
      `  • Copy the partner_archetype tag (specialty_marketer / branded_genericist / etc.)\n\n` +
      `Idempotent — running it multiple times is safe. Never demotes a distributor back to pharma.\n` +
      `Run this AFTER the rescan finishes (pipeline status shows "Ready").`
    )) return;
    try {
      const r = await axios.post(`${API}/imports/${tag}/sync-distributor-category`, {}, { withCredentials: true });
      const d = r.data || {};
      toast.success(
        `Promoted ${d.company_profiles_promoted ?? 0} company_profiles to distributor. ` +
        `(${d.already_distributor ?? 0} were already correct, ${d.no_live_record_found ?? 0} had no matching live record.)`,
        { duration: 12000 }
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2"><FileSpreadsheet className="w-7 h-7 text-sky-600" /> Conference Imports</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Upload an Excel/CSV from any conference (ChinaBio, BIO Intl, JPMorgan, Zesport, etc). Each upload runs through the canonical 5-pass pipeline — classify, sub-classify, fuzzy-match against your CRM, CRO/service audit, then apply the inclusion + exclusion prompts saved in <code className="text-[11px] bg-slate-100 px-1 rounded">/app/memory/import_classification_prompts.md</code>. No setup required.
          </p>
        </div>
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button size="lg" className="bg-sky-600 hover:bg-sky-700" data-testid="open-upload-dialog">
              <Plus className="w-4 h-4 mr-1" /> Upload Conference Sheet
            </Button>
          </DialogTrigger>
          <UploadDialog onClose={() => { setUploadOpen(false); load(); }} />
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="imports-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2.5 text-left">Conference</th>
              <th className="px-4 py-2.5 text-left">Pipeline status</th>
              <th className="px-4 py-2.5 text-right w-32">Total rows</th>
              <th className="px-4 py-2.5 text-right w-32">Approved</th>
              <th className="px-4 py-2.5 text-right w-32">Committed</th>
              <th className="px-4 py-2.5 text-right w-48">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {imports.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400 text-sm">
                No conference imports yet. Click <span className="font-semibold text-sky-700">Upload Conference Sheet</span> to get started.
              </td></tr>
            )}
            {imports.map(i => (
              <tr key={i.import_tag} className="hover:bg-slate-50/50" data-testid={`import-row-${i.import_tag}`}>
                <td className="px-4 py-3">
                  <ImportLabel job={i.job} importTag={i.import_tag} onRenamed={load} />
                  <div className="text-[11px] text-slate-400">{i.import_tag} · {i.job?.filename || 'staged offline'}</div>
                  {i.job?.people_backfill && (
                    <div className={`mt-1 inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] ${i.job.people_backfill.status === 'running' ? 'bg-sky-50 text-sky-800' : i.job.people_backfill.status === 'error' ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-800'}`} data-testid={`people-backfill-status-${i.import_tag}`}>
                      {i.job.people_backfill.status === 'running'
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Backfilling named contacts… {i.job.people_backfill.done || 0}/{i.job.people_backfill.total || '?'}</>
                        : i.job.people_backfill.status === 'error'
                          ? <><AlertCircle className="w-3 h-3" /> Named-contact backfill failed: {i.job.people_backfill.error}</>
                          : <><CheckCircle2 className="w-3 h-3" /> Named contacts backfilled: {i.job.people_backfill.biotech_contacts_added || 0} on biotechs · {i.job.people_backfill.pharma_contacts_added || 0} on pharma · {i.job.people_backfill.companies_touched || 0} companies updated · {i.job.people_backfill.companies_with_people || 0} companies had names in the sheet{(i.job.people_backfill.companies_with_people || 0) > 0 && !(i.job.people_backfill.companies_touched || 0) ? ' (all already present — nothing new to add)' : ''}</>}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {i.historical
                    ? <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid="stage-historical" title="Committed records found in the CRM for this conference tag; staging rows are no longer available."><CheckCircle2 className="w-3 h-3 mr-1" />Imported (historical)</Badge>
                    : <StageBadge job={i.job} />}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{i.total}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold text-emerald-700">{i.approved_pending}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-500">{i.committed}</td>
                <td className="px-4 py-3 text-right">
                  {i.historical ? (
                    <div className="flex items-center justify-end gap-1.5">
                      <Link to={`/biotech?cohort=${encodeURIComponent(i.import_tag)}`}>
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-8" data-testid={`view-cohort-${i.import_tag}`}>
                          View companies <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  ) : (
                  <div className="flex items-center justify-end gap-1.5">
                    {/* While the pipeline is running, show only Cancel inline — everything else moves to the kebab. */}
                    {RUNNING_STAGES.includes(i.job?.stage) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancelImport(i.import_tag)}
                        data-testid={`cancel-${i.import_tag}`}
                        className="text-amber-700 border-amber-200 hover:bg-amber-50 h-8"
                        title="Stop the running classification pipeline."
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> Cancel
                      </Button>
                    )}
                    {/* Primary download stays inline so users don't have to dig for it. */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadExport(i.import_tag, true)}
                      data-testid={`download-approved-${i.import_tag}`}
                      disabled={i.job?.stage && !['ready', 'done', 'error'].includes(i.job.stage)}
                      className="h-8"
                      title="Download approved rows as .xlsx"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" /> Approved.xlsx
                    </Button>
                    {/* All other actions collapse into a kebab so the row stays readable
                        even with the new distributor ops. The menu only shows items
                        relevant to the current pipeline state. */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 px-2"
                          data-testid={`more-actions-${i.import_tag}`}
                          title="More actions"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-60">
                        <DropdownMenuItem
                          onSelect={() => downloadExport(i.import_tag, false)}
                          disabled={i.job?.stage && !['ready', 'done', 'error'].includes(i.job.stage)}
                          data-testid={`download-all-${i.import_tag}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-2" /> Download full .xlsx
                        </DropdownMenuItem>
                        {(!i.job?.stage || ['ready', 'done', 'error', 'empty', 'cancelled'].includes(i.job?.stage)) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={() => triggerBackfillEmails(i.import_tag)}
                              className="text-sky-700 focus:text-sky-800"
                              data-testid={`backfill-emails-${i.import_tag}`}
                            >
                              <MailPlus className="w-3.5 h-3.5 mr-2" /> Backfill emails
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => triggerBackfillPeople(i.import_tag)}
                              className="text-sky-700 focus:text-sky-800"
                              data-testid={`backfill-people-${i.import_tag}`}
                            >
                              <Users className="w-3.5 h-3.5 mr-2" /> Backfill named contacts
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => rescanDistributors(i.import_tag)}
                              className="text-teal-700 focus:text-teal-800"
                              data-testid={`rescan-distributors-${i.import_tag}`}
                            >
                              <Truck className="w-3.5 h-3.5 mr-2" /> Rescan for distributors
                            </DropdownMenuItem>
                            {i.committed > 0 && (
                              <DropdownMenuItem
                                onSelect={() => syncDistributorCategory(i.import_tag)}
                                className="text-indigo-700 focus:text-indigo-800"
                                data-testid={`sync-distributor-${i.import_tag}`}
                              >
                                <ArrowUpCircle className="w-3.5 h-3.5 mr-2" /> Sync distributor category
                              </DropdownMenuItem>
                            )}
                          </>
                        )}
                        {(['cancelled', 'error'].includes(i.job?.stage) ||
                          RUNNING_STAGES.includes(i.job?.stage)) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={() => resumeImport(i.import_tag)}
                              className="text-sky-700 focus:text-sky-800"
                              data-testid={`resume-${i.import_tag}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5 mr-2" /> Resume pipeline
                            </DropdownMenuItem>
                          </>
                        )}
                        {(['cancelled', 'error'].includes(i.job?.stage) || i.committed === 0) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={() => deleteImport(i.import_tag)}
                              className="text-rose-700 focus:text-rose-800"
                              data-testid={`delete-${i.import_tag}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete staging rows
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Link to={`/imports/${i.import_tag}`}>
                      <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-8" data-testid={`review-${i.import_tag}`} disabled={i.job?.stage && !['ready', 'done', 'error'].includes(i.job.stage)}>
                        Review <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </Link>
                  </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function ImportLabel({ job, importTag, onRenamed }) {
  const initial = job?.label || importTag;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) { toast.error('Label cannot be empty'); return; }
    if (trimmed === initial) { setEditing(false); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/imports/${importTag}/job`, { label: trimmed }, { withCredentials: true });
      toast.success(`Renamed to "${trimmed}"`);
      setEditing(false);
      if (onRenamed) onRenamed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    } finally { setBusy(false); }
  };

  const cancel = () => { setValue(initial); setEditing(false); };

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="font-semibold text-slate-900" data-testid={`import-label-${importTag}`}>{initial}</span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-slate-400 hover:text-sky-600 p-0.5 transition-colors"
          title="Rename"
          data-testid={`rename-import-${importTag}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <Input
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
        autoFocus
        disabled={busy}
        className="h-7 text-sm w-64"
        data-testid={`rename-input-${importTag}`}
      />
      <button type="button" onClick={save} disabled={busy} className="text-emerald-600 hover:text-emerald-700 p-0.5" title="Save (Enter)" data-testid={`rename-save-${importTag}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
      </button>
      <button type="button" onClick={cancel} disabled={busy} className="text-slate-400 hover:text-slate-600 p-0.5" title="Cancel (Esc)" data-testid={`rename-cancel-${importTag}`}>
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}


function UploadDialog({ onClose }) {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [tag, setTag] = useState('');
  const [label, setLabel] = useState('');
  const [autoCommit, setAutoCommit] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !tag.trim()) {
      toast.error('Pick a file and an import tag');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('import_tag', tag.trim());
      fd.append('label', label.trim());
      fd.append('auto_commit', autoCommit ? 'true' : 'false');
      const res = await axios.post(`${API}/imports/upload`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      const assets = res.data.asset_row_count || 0;
      toast.success(
        `Uploaded ${res.data.row_count} companies${assets ? ` + ${assets} asset rows` : ''}. ` +
        (autoCommit ? 'Auto import running — follow progress on the import page.' : 'AI cleanup running in background — refresh in 3–5 min.'),
        { duration: 10000 },
      );
      onClose();
      navigate(`/imports/${res.data.import_tag}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="sm:max-w-lg" data-testid="upload-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Upload className="w-4 h-4 text-sky-600" /> Upload Conference Sheet</DialogTitle>
        <DialogDescription>
          Upload an Excel (.xlsx) or CSV. Headers are auto-detected (Company, Email, Country, Sector, Description, Website, LinkedIn — banner rows and partneringONE-style master sheets are handled). Asset rows in the same sheet are imported too.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="file">Sheet file</Label>
          <Input id="file" type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="upload-file-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tag">Import tag <span className="text-slate-400 font-normal">(short slug, e.g. <code>jpm_2027</code>, <code>bio_intl_2026</code>)</span></Label>
          <Input id="tag" value={tag} onChange={e => setTag(e.target.value)} placeholder="bio_intl_2026" data-testid="upload-tag-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="label">Display label <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Input id="label" value={label} onChange={e => setLabel(e.target.value)} placeholder="BIO International 2026" data-testid="upload-label-input" />
        </div>
        <label className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2.5 cursor-pointer" data-testid="upload-auto-commit-toggle">
          <input type="checkbox" className="mt-1 accent-emerald-600" checked={autoCommit} onChange={e => setAutoCommit(e.target.checked)} data-testid="upload-auto-commit-checkbox" />
          <span className="text-sm">
            <span className="font-semibold text-emerald-900">Auto import (hands-free)</span>
            <span className="block text-xs text-emerald-800/80 mt-0.5">
              Classify → commit approved companies → match &amp; commit assets → summary. Rows the rules don't approve stay in the list for manual review.
            </span>
          </span>
        </label>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={busy || !file || !tag.trim()} className="bg-sky-600 hover:bg-sky-700" data-testid="upload-submit-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4 mr-1" /> {autoCommit ? 'Upload & auto import' : 'Upload & start cleanup'}</>}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
